import { supabase } from '../config/supabase';
import { localEventDate } from '../../shared/eventTimezones';
import type { MatchEventRecord, TimeFormat, UserPreferences } from '../types';
import { formatPlayerFirstLast } from './playerPreferences';
import { formatTime, toTimeInput } from './timeFormat';
import { effectiveLineLogistics, matchHasSplitSchedule, type LineLogisticsOverrides } from './lineLogistics';
import { formatInternalLineupMessage } from './lineupMessageFormat.js';

export type CommunicationTeam = {
  id: string;
  team_name: string;
  league_name: string | null;
  home_facility_id: string | null;
  home_match_opponent_message_template: string | null;
};

export function opposingCaptainNames(value: string | null | undefined): string[] {
  return (value || '').split(/[,;\n]+/).map((name) => name.trim()).filter(Boolean);
}

export type CommunicationFacility = {
  facility_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  google_maps_url: string | null;
  warmup_booking_url: string | null;
};

type CaptainAssignmentMessageRow = {
  app_user_id: string;
};

type AppUserMessageRow = {
  id: string;
  display_name: string | null;
};

type LineupPosition = {
  id: string;
  position_code: string;
};

type LineupEntry = {
  league_configuration_position_id: string;
  slot_number: number;
  player_id: string;
};

type LineupStatus = {
  league_configuration_position_id: string;
  line_status: 'normal' | 'default_by_opponent' | 'default_by_us';
} & LineLogisticsOverrides;

type LineupPlayer = {
  id: string;
  first_name: string | null;
  last_name: string | null;
};

export const defaultCaptainWelcomeTemplate = `This is {CaptainNames} from the {TeamName} team of the {LeagueName}. I'm confirming our match time of {MatchTime} on {MatchDate} at {FacilityName} ({GoogleMapsLink}).

I'll confirm the court numbers in the next few days and send them. Your team can warm up on the match courts starting {WarmupMinutes} minutes prior to match time. If you want additional warm-up court time you can use the Impact Activities App to book it. ({WarmupBookingLink}).

Water and snacks are available in the pro shop but not on the courts themselves. Please let me know if you have any questions about the facility. Looking forward to it!`;

export function formatCommunicationDate(value: string | null | undefined, withWeekday = false): string {
  if (!value) return '';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    weekday: withWeekday ? 'short' : undefined,
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatMatchSelectDate(value: string | null | undefined): string {
  if (!value) return '';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function communicationFacilityAddress(facility: CommunicationFacility | null): string {
  if (!facility) return '';
  const cityStateZip = [facility.city, [facility.state, facility.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [facility.address_line1, facility.address_line2, cityStateZip].filter(Boolean).join(', ');
}

export function applyCommunicationTemplate(template: string, tokens: Record<string, string>): string {
  return template.replace(/\{([A-Za-z]+)\}/g, (_match, token: string) => tokens[token] ?? '');
}

function formatNameList(names: string[]): string {
  if (names.length <= 1) return names[0] || '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function warmupMinutes(matchTime: string | null | undefined, warmupTime: string | null | undefined): string {
  const match = toTimeInput(matchTime || null);
  const warmup = toTimeInput(warmupTime || null);
  if (!match || !warmup) return '';
  const [matchHours, matchMinutes] = match.split(':').map(Number);
  const [warmupHours, warmupMins] = warmup.split(':').map(Number);
  if ([matchHours, matchMinutes, warmupHours, warmupMins].some((value) => !Number.isFinite(value))) return '';
  const matchTotal = matchHours * 60 + matchMinutes;
  const warmupTotal = warmupHours * 60 + warmupMins;
  const diff = matchTotal - warmupTotal;
  return diff >= 0 ? String(diff) : '';
}

function lineStatusLabel(lineStatus: LineupStatus['line_status']): string {
  if (lineStatus === 'default_by_opponent') return 'Default by Opponent';
  if (lineStatus === 'default_by_us') return 'Default by Us';
  return 'Normal';
}

export async function loadCommunicationFacility(facilityId: string | null): Promise<CommunicationFacility | null> {
  if (!facilityId) return null;
  const { data, error } = await supabase
    .from('facilities')
    .select('facility_name, address_line1, address_line2, city, state, zip_code, google_maps_url, warmup_booking_url')
    .eq('id', facilityId)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data || null) as CommunicationFacility | null;
}

export async function loadCaptainNamesForCommunication(teamId: string): Promise<string> {
  const { data: assignmentData, error: assignmentError } = await supabase
    .from('team_captain_assignments')
    .select('app_user_id, assignment_role, is_primary_captain, created_at')
    .eq('team_id', teamId)
    .eq('is_active', true)
    .is('deleted_at', null)
    .in('assignment_role', ['captain', 'co_captain', 'assistant_captain'])
    .order('is_primary_captain', { ascending: false });
  if (assignmentError) throw new Error(assignmentError.message);

  const assignments = ((assignmentData || []) as unknown as CaptainAssignmentMessageRow[]).filter((row) => row.app_user_id);
  const appUserIds = [...new Set(assignments.map((row) => row.app_user_id))];
  if (appUserIds.length === 0) return '';

  const { data: appUserData, error: appUserError } = await supabase
    .from('app_users')
    .select('id, display_name')
    .in('id', appUserIds)
    .eq('is_active', true)
    .is('deleted_at', null);
  if (appUserError) throw new Error(appUserError.message);

  const appUserById = new Map(((appUserData || []) as unknown as AppUserMessageRow[]).map((user) => [user.id, user.display_name || '']));
  const names = assignments.map((assignment) => appUserById.get(assignment.app_user_id) || '').filter(Boolean);
  return formatNameList([...new Set(names)]);
}

export async function buildCaptainWelcomeMessage(match: MatchEventRecord, team: CommunicationTeam | null, timeFormat: TimeFormat): Promise<{ message: string; warnings: string[] }> {
  const template = team?.home_match_opponent_message_template?.trim() || defaultCaptainWelcomeTemplate;
  const facilityId = match.facility_id || (match.match_site_type === 'Home' ? team?.home_facility_id || null : null);
  const [facility, captainNames] = await Promise.all([
    loadCommunicationFacility(facilityId || null),
    loadCaptainNamesForCommunication(match.team_id),
  ]);
  const tokens = {
    CaptainNames: captainNames,
    TeamName: team?.team_name || '',
    LeagueName: team?.league_name || '',
    OpponentTeam: match.opponent_name || '',
    MatchDate: formatCommunicationDate(localEventDate(match), true),
    MatchTime: formatTime(match.match_time, timeFormat, match),
    WarmupTime: formatTime(match.warmup_time, timeFormat, match),
    WarmupMinutes: warmupMinutes(match.match_time, match.warmup_time),
    FacilityName: facility?.facility_name || '',
    FacilityAddress: communicationFacilityAddress(facility),
    GoogleMapsLink: facility?.google_maps_url || '',
    CourtNumbers: match.court_numbers || '',
    WarmupBookingLink: facility?.warmup_booking_url || '',
  };
  const warnings = [
    !match.match_time ? 'missing match time' : '',
    !match.match_date ? 'missing match date' : '',
    !facilityId || !facility ? 'missing facility' : '',
    !facility?.google_maps_url ? 'missing Google Maps link' : '',
    !captainNames ? 'No active captains found for this team.' : '',
    template.includes('{WarmupBookingLink}') && !facility?.warmup_booking_url ? 'missing warmup booking link' : '',
  ].filter(Boolean);
  return { message: applyCommunicationTemplate(template, tokens), warnings };
}

export async function buildLineupMessage(
  match: MatchEventRecord,
  teamName: string,
  userPreferences: UserPreferences,
  timeFormat: TimeFormat,
  external: boolean,
  facilityIdOverride?: string | null,
): Promise<string> {
  const [facility, positionsResult, entriesResult, statusesResult] = await Promise.all([
    external ? Promise.resolve(null) : loadCommunicationFacility(facilityIdOverride || match.facility_id || null),
    supabase
      .from('league_configuration_positions')
      .select('id, position_code, display_order')
      .eq('league_configuration_id', match.league_configuration_id)
      .eq('is_active', true)
      .is('deleted_at', null)
      .order('display_order', { ascending: true }),
    supabase
      .from('lineup_entries')
      .select('league_configuration_position_id, slot_number, player_id')
      .eq('match_event_id', match.id)
      .is('deleted_at', null),
    supabase
      .from('lineup_position_statuses')
      .select('league_configuration_position_id, line_status, scheduled_date_override, warmup_time_override, start_time_override, facility_id_override, location_text_override, court_assignment, logistics_changed_after_publish')
      .eq('match_event_id', match.id),
  ]);

  if (positionsResult.error || entriesResult.error || statusesResult.error) {
    throw new Error(positionsResult.error?.message || entriesResult.error?.message || statusesResult.error?.message || 'Unable to build lineup message.');
  }

  const entries = (entriesResult.data || []) as unknown as LineupEntry[];
  const playerIds = [...new Set(entries.map((entry) => entry.player_id))];
  const playersResult = playerIds.length > 0
    ? await supabase.from('players').select('id, first_name, last_name').in('id', playerIds).is('deleted_at', null)
    : { data: [], error: null };
  if (playersResult.error) throw new Error(playersResult.error.message);

  const playersById = new Map(((playersResult.data || []) as unknown as LineupPlayer[]).map((player) => [player.id, player]));
  const statusRows = (statusesResult.data || []) as unknown as LineupStatus[];
  const statusesByPosition = new Map(statusRows.map((status) => [status.league_configuration_position_id, status]));
  const overrideFacilityIds = [...new Set(statusRows.map((status) => status.facility_id_override).filter(Boolean))] as string[];
  const overrideFacilitiesResult = overrideFacilityIds.length
    ? await supabase.from('facilities').select('id, facility_name').in('id', overrideFacilityIds).is('deleted_at', null)
    : { data: [], error: null };
  if (overrideFacilitiesResult.error) throw new Error(overrideFacilitiesResult.error.message);
  if (!external) {
    return formatInternalLineupMessage({
      match,
      teamName,
      facility,
      positions: (positionsResult.data || []) as unknown as LineupPosition[],
      entries,
      statuses: statusRows,
      players: (playersResult.data || []) as unknown as LineupPlayer[],
      overrideFacilities: (overrideFacilitiesResult.data || []) as Array<{ id: string; facility_name: string | null }>,
      timeFormat,
    });
  }
  const overrideFacilityNames = new Map(((overrideFacilitiesResult.data || []) as Array<{ id: string; facility_name: string | null }>).map((item) => [item.id, item.facility_name || 'Facility']));
  const entriesByPosition = entries.reduce<Record<string, LineupEntry[]>>((acc, entry) => {
    acc[entry.league_configuration_position_id] ||= [];
    acc[entry.league_configuration_position_id].push(entry);
    return acc;
  }, {});
  const date = formatCommunicationDate(localEventDate(match));
  const matchTime = formatTime(match.match_time, timeFormat, match);
  const warmupTime = formatTime(match.warmup_time, timeFormat, match);
  const facilityName = facility?.facility_name || 'the facility';
  const header = external
    ? `Here is the ${teamName} lineup for ${date}${matchTime ? ` at ${matchTime}` : ''}.`
    : `Here is our lineup for ${match.opponent_name || 'TBD'} at ${facilityName} on ${date}${matchTime ? ` at ${matchTime}` : ''}.`;
  const internalHeader = [
    header,
    warmupTime ? `Please arrive by ${warmupTime} for warmup.` : '',
    facility?.google_maps_url ? `(${facility.google_maps_url})` : '',
  ].filter(Boolean).join(' ');
  const lines = [external ? header : internalHeader];
  lines.push('');
  const splitSchedule = matchHasSplitSchedule(statusRows);
  lines.push(
    ...((positionsResult.data || []) as unknown as LineupPosition[]).map((position) => {
      const status = statusesByPosition.get(position.id);
      const lineStatus = status?.line_status || 'normal';
      if (lineStatus !== 'normal') return `${position.position_code}: ${lineStatusLabel(lineStatus)}`;
      const names = (entriesByPosition[position.id] || [])
        .sort((a, b) => a.slot_number - b.slot_number)
        .map((entry) => formatPlayerFirstLast(playersById.get(entry.player_id)))
        .filter(Boolean)
        .join(' / ');
      const logistics = effectiveLineLogistics(match, status);
      const detail = splitSchedule
        ? [
            logistics.date ? formatCommunicationDate(localEventDate({ match_date: logistics.date, match_time: logistics.startTime, timezone: match.timezone }), false) : '',
            logistics.warmupTime ? `Warmup ${formatTime(logistics.warmupTime, timeFormat, { match_date: logistics.date, timezone: match.timezone })}` : '',
            logistics.startTime ? formatTime(logistics.startTime, timeFormat, { match_date: logistics.date, timezone: match.timezone }) : '',
            logistics.facilityId && logistics.facilityId !== match.facility_id ? overrideFacilityNames.get(logistics.facilityId) || '' : logistics.locationText || '',
            logistics.courtAssignment ? `Court ${logistics.courtAssignment}` : '',
          ].filter(Boolean).join(' · ')
        : logistics.courtAssignment ? `Court ${logistics.courtAssignment}` : '';
      return `${position.position_code}: ${names || 'TBD'}${detail ? ` — ${detail}` : ''}`;
    }),
  );
  return lines.join('\n');
}
